import type { NotificationsApi, PushSubscriptionRecord } from "../entities/notifications";
import { registerServiceWorker } from "../pwa/serviceWorker";

export class WebPushManager {
  constructor(private readonly api: Pick<NotificationsApi, "registerPush" | "unregisterPush">) {}

  /**
   * Подписан ли этот браузер.
   *
   * Спрашиваем сам браузер, а не сервер: подписка принадлежит устройству, и
   * на другом компьютере того же человека её нет. Кнопка иначе выглядела
   * одинаково и до включения, и после.
   */
  async subscribedHere(): Promise<boolean> {
    return (await this.localSubscription()) != null;
  }

  /**
   * Отпечаток подписки этого устройства.
   *
   * По нему интерфейс узнаёт своё устройство в общем списке: сервер отдаёт
   * такой же отпечаток, а сам адрес подписки не отдаёт никому — это ключ к
   * доставке. Возвращает null, если это устройство не подписано.
   */
  async currentFingerprint(): Promise<string | null> {
    const subscription = await this.localSubscription();
    if (subscription === null) return null;

    return fingerprint(subscription.endpoint);
  }

  /** Отписать это устройство и убрать его запись на сервере. */
  async unsubscribeHere(recordId: string): Promise<void> {
    await this.api.unregisterPush(recordId);
    await (await this.localSubscription())?.unsubscribe();
  }

  /**
   * Убрать чужое устройство.
   *
   * Подписку в этом браузере не трогает: она принадлежит другому устройству, и
   * отписывать здесь нечего.
   */
  async forget(recordId: string): Promise<void> {
    await this.api.unregisterPush(recordId);
  }

  private localSubscription(): Promise<PushSubscription | null> {
    return localPushSubscription();
  }

  /** Что ответил браузер на просьбу показывать уведомления. */
  permission(): NotificationPermission | "unsupported" {
    return pushPermission();
  }

  /**
   * Показать уведомление прямо из браузера, не спрашивая сервер.
   *
   * Так отличают две разные поломки: доставку с сервера и показ на стороне
   * операционной системы. Если это уведомление не появилось, дело не в ERP —
   * показ запрещён в браузере или в настройках самого компьютера.
   */
  async showLocalProbe(): Promise<void> {
    if (!this.supported() || Notification.permission !== "granted") return;
    const registration =
      (await navigator.serviceWorker.getRegistration("/")) ??
      (await registerServiceWorker(navigator.serviceWorker));
    await registration.showNotification("Проверка уведомлений", {
      body: "Так выглядят уведомления ERP на этом устройстве.",
      tag: "erp-probe",
    });
  }

  supported(): boolean {
    return pushSupported();
  }

  /**
   * Просит у человека разрешение показывать уведомления.
   *
   * Вызывать нужно первым делом в обработчике нажатия, до любых запросов к
   * серверу: Safari на iPhone и iPad открывает диалог разрешения только из
   * самого жеста, а после ожидания сети жест уже считается закончившимся.
   * Если разрешение уже есть, вызов ничего не показывает и сразу возвращается.
   */
  async ensurePermission(): Promise<void> {
    if (!this.supported()) {
      throw new Error("Web Push требует защищённого HTTPS-подключения и поддержки Service Worker. В Chrome, Edge и Firefox это доступно по HTTPS; на iPhone/iPad — в установленном веб‑приложении.");
    }
    // Разрешение уже есть — ничего не спрашиваем. Safari на iPhone на повторный
    // вызов вне жеста отвечает «не разрешено», хотя в настройках оно выдано, и
    // подключение падало с ложным отказом.
    if (currentPermission() === "granted") return;
    const answer = await Notification.requestPermission();
    // Ответ функции и текущее состояние бывают разными (тот же Safari), поэтому
    // верим состоянию: отказом считаем, только если разрешения нет и там.
    if (answer !== "granted" && currentPermission() !== "granted") {
      throw new Error("Разрешение на уведомления не предоставлено. Обычные уведомления в ERP продолжат работать.");
    }
  }

  async subscribe(vapidPublicKey: string, deviceName?: string): Promise<PushSubscriptionRecord> {
    await this.ensurePermission();
    // Тот же service worker, что кеширует оболочку: у устройства одна фоновая
    // служба, а значит, и одна подписка. Ждём его активации: подписаться через
    // ещё не активный worker браузер не даёт.
    await registerServiceWorker(navigator.serviceWorker);
    const registration = await navigator.serviceWorker.ready;
    // Подписка в браузере могла остаться от прошлого раза — например, её
    // заводили под другим сотрудником. Тогда сервер о ней не знает, а
    // повторная подписка тем же ключом вернула бы ту же самую. Берём
    // существующую и заново записываем её на сервере: так «Подключить»
    // всегда чинит рассинхрон, а не упирается в него.
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeBase64Url(vapidPublicKey),
      }));
    const json = subscription.toJSON();
    if (json.endpoint === undefined || json.keys?.p256dh === undefined || json.keys.auth === undefined) {
      throw new Error("The browser returned an incomplete push subscription.");
    }
    const response = await this.api.registerPush({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      content_encoding: "aes128gcm",
      device_name: deviceName,
    });

    return response.data;
  }

}

/** Умеет ли это устройство получать push: нужен HTTPS, service worker и PushManager. */
export function pushSupported(): boolean {
  return window.isSecureContext && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
}

/** Состояние разрешения на момент вызова: читаем заново, а не из ранее суженного типа. */
function currentPermission(): NotificationPermission {
  return Notification.permission;
}

/** Что ответил браузер на просьбу показывать уведомления. */
export function pushPermission(): NotificationPermission | "unsupported" {
  return pushSupported() ? Notification.permission : "unsupported";
}

/** Подписка этого браузера — та, что принадлежит устройству, а не серверу. */
export async function localPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");

  return (await registration?.pushManager.getSubscription()) ?? null;
}

/**
 * Отпечаток адреса подписки — те же тридцать два знака, что считает сервер.
 */
export async function fingerprint(endpoint: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(endpoint));

  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}

export function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const decoded = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(decoded.length));
  for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index);
  return bytes;
}
